import { useEffect, useRef, useState } from 'react';
import { api } from '../lib/api.js';

/**
 * One advertising slot.
 *
 * Renders nothing at all unless the server says this household should see ads.
 * That decision is not made here on purpose: a client working out its own
 * eligibility is a client that can be wrong about somebody's opt-out, and the
 * plan and the preference both already live on the server.
 *
 * Nothing is fetched and no third-party script is loaded until a slot actually
 * mounts, so a paid household never pays the network cost of an ad stack it is
 * not shown.
 */

// One request for the whole app rather than one per slot. Two ads on a page
// should not be two round trips, and they cannot disagree with each other.
let settingsPromise = null;
function adSettings() {
  if (!settingsPromise) {
    settingsPromise = api.get('/ads').catch(() => ({ show: false }));
  }
  return settingsPromise;
}

let scriptPromise = null;
/**
 * Load Google's tag, once.
 *
 * `requestNonPersonalizedAds` is set BEFORE the script is inserted. It is read
 * off the queue when the tag initialises, so setting it afterwards would be a
 * personalised impression served to somebody who opted out -- which is the
 * failure this whole path exists to prevent, and it would be invisible.
 */
function loadAdSense(client, personalized) {
  if (!scriptPromise) {
    window.adsbygoogle = window.adsbygoogle || [];
    if (!personalized) window.adsbygoogle.requestNonPersonalizedAds = 1;

    scriptPromise = new Promise((resolve, reject) => {
      const tag = document.createElement('script');
      tag.async = true;
      tag.crossOrigin = 'anonymous';
      tag.src = `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${encodeURIComponent(client)}`;
      tag.onload = resolve;
      // A blocked or failed tag is not an error worth showing anybody. The slot
      // stays empty and the page is otherwise unaffected.
      tag.onerror = () => reject(new Error('ad tag failed to load'));
      document.head.appendChild(tag);
    });
  }
  return scriptPromise;
}

export default function AdSlot({ name, label = 'Advertisement' }) {
  const [settings, setSettings] = useState(null);
  const slotRef = useRef(null);
  // StrictMode mounts twice in development, and pushing the same <ins> twice
  // makes the tag throw "already have ads in them".
  const pushed = useRef(false);

  useEffect(() => {
    let alive = true;
    adSettings().then((value) => { if (alive) setSettings(value); });
    return () => { alive = false; };
  }, []);

  const slotId = settings?.show ? settings.slots?.[name] : null;

  useEffect(() => {
    if (!slotId || pushed.current || !slotRef.current) return;
    pushed.current = true;
    loadAdSense(settings.client, settings.personalized)
      .then(() => { (window.adsbygoogle = window.adsbygoogle || []).push({}); })
      .catch(() => { /* blocked or offline; the slot stays empty */ });
  }, [slotId, settings]);

  // A placement with no slot id configured has no ad, which is a normal state
  // while one placement is live and another is not.
  if (!slotId) return null;

  return (
    <aside className="ad-slot" aria-label={label}>
      {/* Labelled, and labelled honestly. A native-looking advertisement inside
          a recipe list is the thing people resent, and it is the thing that
          gets an AdSense account suspended for deceptive placement. */}
      <span className="ad-slot-label small muted">{label}</span>
      <ins
        ref={slotRef}
        className="adsbygoogle"
        style={{ display: 'block' }}
        data-ad-client={settings.client}
        data-ad-slot={slotId}
        data-ad-format="auto"
        data-full-width-responsive="true"
      />
    </aside>
  );
}
